import React from 'react';
import { AlertTriangleIcon } from 'lucide-react';
import { PageHero } from '../components/ui/PageHero';
import { Container, Eyebrow, Heading } from '../components/ui/Section';
import { ServiceList } from '../components/ui/ServiceGroups';
import { CtaBand } from '../components/ui/CtaBand';
import { ArgentinaCoverageMap } from '../components/features/ArgentinaCoverageMap';
import { OperationFlow } from '../components/features/OperationFlow';
import { Faq } from '../components/ui/Faq';
import { IMAGES, TRANSPORT_CAPACITIES, TRANSPORT_SERVICES } from '../data/solutions';
import { faqJsonLd, serviceJsonLd, useSeo } from '../utils/seo';
const FAQS = [{
  question: '¿Qué capacidad de transporte puede ofrecer Rabemar?',
  answer: 'El modelo combina 20 unidades de activos fijos con una red 3PL, con más de 150 unidades habilitadas disponibles para distintos tipos de operación en Argentina.'
}, {
  question: '¿Qué tipos de unidades se utilizan?',
  answer: 'Bateas de hasta 35 TN, chasis y acoplado de aproximadamente 30 TN con capacidad de hasta 28 pallets, y alternativas desde vehículos de aproximadamente 500 kg hasta transporte pesado.'
}, {
  question: '¿Cuál es el nivel de servicio objetivo?',
  answer: 'El objetivo operativo del modelo es 90% dentro de las 72 horas, buscando completar operaciones en 48 horas cuando las condiciones lo permiten. Es un indicador del modelo operativo, no una garantía contractual universal: el compromiso final se define por operación.'
}, {
  question: '¿Se pueden transportar cargas IMO?',
  answer: 'Sí, cuando corresponda y con las habilitaciones, unidades y protocolos requeridos para el tipo de mercadería y el trayecto.'
}];
const CRUMBS = [{
  name: 'Inicio',
  path: '/'
}, {
  name: 'Soluciones',
  path: '/soluciones'
}, {
  name: 'Transporte & Distribución',
  path: '/soluciones/transporte-distribucion'
}];
export function Transport() {
  useSeo({
    title: 'Transporte de cargas en Argentina | Distribución y gestión de transportistas — Rabemar',
    description: 'Transporte terrestre local y nacional, operaciones multimodales, cargas generales e industriales, flota propia y red 3PL con gestión integral de transportistas.',
    path: '/soluciones/transporte-distribucion',
    image: IMAGES.fleet,
    breadcrumbs: CRUMBS,
    jsonLd: [serviceJsonLd('Transporte y distribución de cargas', 'Transporte terrestre local y nacional, operaciones multimodales, cargas generales e industriales, modelo de flota propia y outsourcing 3PL.', '/soluciones/transporte-distribucion'), faqJsonLd(FAQS)]
  });
  return <>
      <PageHero eyebrow="02 — Transporte & Distribución" title="Capacidad de transporte para todo el territorio argentino." lead="Un modelo mixto de activos fijos y red 3PL, con gestión integral de transportistas, control documental y seguimiento de cada viaje." image={IMAGES.fleet} imageAlt="Flota de camiones con semirremolques estacionados en fila en un playón de distribución" crumbs={CRUMBS} ctaLabel="Solicitar capacidad de transporte" ctaService="Transporte" stats={[{
      value: '20',
      label: 'Unidades de activos fijos'
    }, {
      value: '+150',
      label: 'Unidades habilitadas disponibles'
    }, {
      value: '35 TN',
      label: 'Capacidad máxima en bateas'
    }, {
      value: '28',
      label: 'Pallets por unidad'
    }]} data-id="element-891" data-mp="1qa3cvjvpf73b" />

      <section className="bg-white py-20 md:py-28" data-id="element-892" data-mp="2o44cuzh2prhz">
        <Container data-id="element-893" data-mp="2j3zsuzgyien0">
          <div className="grid gap-12 lg:grid-cols-[1fr_1fr] lg:gap-20" data-id="element-894" data-mp="2j3glgcoxn8n8">
            <div data-id="element-895" data-mp="2j5syqcjqwuty">
              <Eyebrow data-id="element-896" data-mp="2e559nwf2uo2d">Servicios de transporte</Eyebrow>
              <Heading as="h2" size="md" className="mt-5" data-id="element-897" data-mp="2e4n8qmhgzlo4">
                Local, nacional y multimodal.
              </Heading>
              <p className="mt-5 text-lg leading-relaxed text-graphite-500" data-id="element-898" data-mp="12pq6gaw9yzyt">
                Cada operación se arma según la carga, el trayecto y el nivel de servicio requerido, con la unidad
                adecuada y la documentación verificada antes de salir.
              </p>
              <div className="mt-9" data-id="element-899" data-mp="2vf90b7p9dfzo">
                <ServiceList items={TRANSPORT_SERVICES} data-id="element-900" data-mp="2hm9j919tvdq0" />
              </div>
            </div>

            <div data-id="element-901" data-mp="2mkb33of2vh2j">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-graphite-500" data-id="element-902" data-mp="2mlvzljkyda31">
                Capacidades documentadas
              </p>
              <dl className="mt-5 divide-y divide-mist-200 border-y border-mist-200" data-id="element-903" data-mp="3ec0fb4xrp8xx">
                {TRANSPORT_CAPACITIES.map(cap => <div key={cap.label} className="flex items-baseline justify-between gap-6 py-5" data-id="element-904" data-mp="30b65chv2mzx5">
                    <dt className="text-sm text-graphite-500" data-id="element-905" data-mp="35a9cc7um3g7r">{cap.label}</dt>
                    <dd className="font-display text-xl font-bold tracking-tight text-graphite-900" data-id="element-906" data-mp="1owj6avjxmhvh">{cap.value}</dd>
                  </div>)}
              </dl>
              <p className="mt-6 flex gap-2 border border-mist-200 bg-mist-50 p-4 text-xs leading-relaxed text-graphite-500" data-id="element-907" data-mp="1ttsu66roalh7">
                <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent-600" aria-hidden="true" data-id="element-908" data-mp="1he2updsepycf" />
                <span data-id="element-909" data-mp="1vc9k05jaiq0h">
                  Las capacidades corresponden al modelo operativo documentado. La disponibilidad concreta de unidades se
                  confirma por operación según ruta, tipo de carga y ventana horaria.
                </span>
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="bg-graphite-900 py-20 md:py-28" data-id="element-910" data-mp="yowfw1uqhqvw">
        <Container data-id="element-911" data-mp="1cfi4m76vcv1p">
          <div className="grid items-center gap-14 lg:grid-cols-[1fr_1fr]" data-id="element-912" data-mp="1cg1c0tyw811h">
            <div data-id="element-913" data-mp="17mozvjbehmpm">
              <Eyebrow tone="dark" data-id="element-914" data-mp="17m6unx6rfy5k">Cobertura</Eyebrow>
              <Heading as="h2" size="lg" tone="dark" className="mt-5" data-id="element-915" data-mp="jxw1zhb7x4sd">
                Todo el territorio argentino.
              </Heading>
              <p className="mt-6 text-lg leading-relaxed text-mist-200" data-id="element-916" data-mp="eyb131chmmd0">
                Desde la base en Pergamino coordinamos corredores hacia el litoral, el centro, Cuyo, el norte y toda la
                Patagonia, combinando flota propia y transportistas habilitados.
              </p>
              <div className="mt-9 border border-white/15 bg-white/[0.04] p-6" data-id="element-917" data-mp="2osgac3d8wl1r">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-accent-400" data-id="element-918" data-mp="2tqhrujv0wcb4">SLA objetivo del modelo</p>
                <p className="mt-4 font-display text-3xl font-bold tracking-tight text-white" data-id="element-919" data-mp="1w3rfgm2s9byg">
                  90% dentro de las 72 horas
                </p>
                <p className="mt-3 text-sm leading-relaxed text-mist-200" data-id="element-920" data-mp="260vxqaticn9z">
                  Buscando completar operaciones en 48 horas cuando las condiciones lo permiten. Es un indicador de
                  capacidad del modelo operativo, no una garantía contractual universal: el compromiso se define en cada
                  acuerdo de servicio.
                </p>
              </div>
            </div>
            <div className="flex justify-center" data-id="element-921" data-mp="19uke01t883ex">
              <ArgentinaCoverageMap data-id="element-922" data-mp="19t9eie6tdhtm" />
            </div>
          </div>
        </Container>
      </section>

      <section className="bg-white py-20 md:py-28" data-id="element-923" data-mp="15ujzyia1hu8e">
        <Container data-id="element-924" data-mp="110xp8vnfs0f8">
          <div className="max-w-3xl" data-id="element-925" data-mp="11061u83zt901">
            <Eyebrow data-id="element-926" data-mp="w2nq69ylcfoc">Gestión de transportistas</Eyebrow>
            <Heading as="h2" size="md" className="mt-5" data-id="element-927" data-mp="w1usdyhm10ic">
              Cada viaje, con la documentación y el seguimiento resueltos.
            </Heading>
            <p className="mt-5 text-lg leading-relaxed text-graphite-500" data-id="element-928" data-mp="m4q3vs0aksyd">
              La gestión de transportistas incluye habilitación, control documental de unidad y chofer, seguimiento del
              viaje, gestión de contingencias y medición del cumplimiento.
            </p>
          </div>
          <div className="mt-10" data-id="element-929" data-mp="2cgm7xatnqda8">
            <OperationFlow tone="light" data-id="element-930" data-mp="2cfkxn8kgtutk" />
          </div>
        </Container>
      </section>

      <section className="bg-mist-50 py-20 md:py-28" data-id="element-931" data-mp="27iuicnghj397">
        <Container data-id="element-932" data-mp="27fygwp5fhwg0">
          <div className="grid gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16" data-id="element-933" data-mp="1gbx9q6uvlb37">
            <div data-id="element-934" data-mp="1lbjz1enzahsd">
              <Eyebrow data-id="element-935" data-mp="1lc1rs1bfmdvj">Preguntas frecuentes</Eyebrow>
              <Heading as="h2" size="md" className="mt-5" data-id="element-936" data-mp="1laqmty69302u">
                Transporte & Distribución
              </Heading>
            </div>
            <Faq items={FAQS} data-id="element-937" data-mp="1blgxgq2sjvif" />
          </div>
        </Container>
      </section>

      <CtaBand title="¿Necesita capacidad de transporte?" text="Indíquenos origen, destino, tipo de carga y volumen: confirmamos unidad, esquema de servicio y niveles de cumplimiento." primaryLabel="Solicitar capacidad de transporte" location="transporte-cta" service="Transporte" data-id="element-938" data-mp="xlz2fsywjm0m" />
    </>;
}